import { useState } from "react";
import axios from "axios";
import { useTheme } from "@/components/ThemeContext";
import SEOHead from "@/components/SEOHead";
import { PrenotazioneStructuredData } from "@/components/StructuredData";
import { CalendarDays, Send, Phone, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import ScrollReveal from "@/components/ScrollReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "Transfer Aeroportuale",
  "Transfer Stazione",
  "Evento / Cerimonia",
  "Business Travel",
  "Tour Personalizzato",
  "Altro",
];

export default function PrenotazionePage() {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefono: "",
    data: "",
    luogo_partenza: "",
    destinazione: "",
    servizio: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { isDark } = useTheme();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome || !formData.email || !formData.telefono || !formData.data || !formData.luogo_partenza || !formData.destinazione) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      setSuccess(true);
      toast.success("Richiesta inviata con successo!");
      setFormData({
        nome: "",
        email: "",
        telefono: "",
        data: "",
        luogo_partenza: "",
        destinazione: "",
        servizio: "",
        note: "",
      });
    } catch {
      toast.error("Errore nell'invio. Riprova o contattaci telefonicamente.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main data-testid="prenotazione-page" className="pt-20">
        <section className={`py-20 md:py-32 min-h-[80vh] flex items-center transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <ScrollReveal>
              <div className="inline-flex items-center justify-center w-20 h-20 border-2 border-[#C9A646] mb-8">
                <Check className="w-10 h-10 text-[#C9A646]" strokeWidth={1.5} />
              </div>
              <h1 className="text-4xl sm:text-5xl tracking-tight font-normal text-white mb-6">
                Richiesta <span className="italic text-[#C9A646]">Inviata</span>
              </h1>
              <p className="text-base text-[#A1A1AA] leading-relaxed mb-8">
                Grazie per averci contattato. Vi risponderemo nel più breve tempo possibile
                con un preventivo personalizzato. Per urgenze, non esitate a chiamarci.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="tel:+393784035054"
                  data-testid="success-call-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  Chiama Ora
                </a>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="new-request-btn"
                  className="inline-flex items-center justify-center gap-2 border border-[#C9A646] text-[#C9A646] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#C9A646]/10 transition-all duration-300"
                >
                  Nuova Richiesta
                </button>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="prenotazione-page" className="pt-20">
      <SEOHead
        title="Prenota il Tuo Transfer - Preventivo Gratuito"
        description="Prenota il tuo transfer NCC ad Avellino. Richiedi un preventivo personalizzato e gratuito per transfer aeroportuali, stazioni, eventi o tour. Risposta rapida garantita. Chiama +39 378 4035054."
        path="/prenota"
      />
      <PrenotazioneStructuredData />
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left - Info */}
            <div className="lg:col-span-5">
              <ScrollReveal>
                <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-6">
                  Prenotazione
                </span>
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl tracking-tight font-normal leading-[1.1] mb-8 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
                  Richiedi il tuo
                  <span className="italic text-[#C9A646]"> preventivo</span>
                </h1>
                <p className={`text-base leading-relaxed mb-10 ${isDark ? "text-[#A1A1AA]" : "text-[#555]"}`}>
                  Compila il modulo con i dettagli del tuo viaggio. Ti risponderemo
                  in tempi brevissimi con un preventivo personalizzato e senza impegno.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.1}>
                <div className={`border p-8 ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}>
                  <h3 className={`text-lg font-medium mb-4 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>Preferisci chiamare?</h3>
                  <p className={`text-sm mb-6 ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                    Il nostro team è a disposizione H24 per rispondere alle vostre richieste.
                  </p>
                  <a
                    href="tel:+393784035054"
                    data-testid="form-call-btn"
                    className="inline-flex items-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-6 py-3 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
                  >
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    +39 378 4035054
                  </a>
                </div>
              </ScrollReveal>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-7">
              <ScrollReveal delay={0.15}>
                <form
                  onSubmit={handleSubmit}
                  data-testid="quote-form"
                  className={`border p-8 md:p-10 ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="nome" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Nome e Cognome *
                      </Label>
                      <Input
                        id="nome"
                        data-testid="form-nome"
                        value={formData.nome}
                        onChange={(e) => handleChange("nome", e.target.value)}
                        placeholder="Mario Rossi"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="form-email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="mario@email.com"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="telefono" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Telefono *
                      </Label>
                      <Input
                        id="telefono"
                        type="tel"
                        data-testid="form-telefono"
                        value={formData.telefono}
                        onChange={(e) => handleChange("telefono", e.target.value)}
                        placeholder="+39 333 1234567"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="data" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Data del Transfer *
                      </Label>
                      <Input
                        id="data"
                        type="date"
                        data-testid="form-data"
                        value={formData.data}
                        onChange={(e) => handleChange("data", e.target.value)}
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A]"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="partenza" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Luogo di Partenza *
                      </Label>
                      <Input
                        id="partenza"
                        data-testid="form-partenza"
                        value={formData.luogo_partenza}
                        onChange={(e) => handleChange("luogo_partenza", e.target.value)}
                        placeholder="Es. Aeroporto di Napoli"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="destinazione" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Destinazione *
                      </Label>
                      <Input
                        id="destinazione"
                        data-testid="form-destinazione"
                        value={formData.destinazione}
                        onChange={(e) => handleChange("destinazione", e.target.value)}
                        placeholder="Es. Avellino Centro"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                      Tipo di Servizio
                    </Label>
                    <Select
                      value={formData.servizio}
                      onValueChange={(val) => handleChange("servizio", val)}
                    >
                      <SelectTrigger
                        data-testid="form-servizio"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A]"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                      >
                        <SelectValue placeholder="Seleziona servizio" />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-[#141414] border-white/10" : "bg-white border-black/10"}>
                        {serviceOptions.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            className={`hover:bg-[#C9A646]/10 focus:bg-[#C9A646]/10 ${isDark ? "text-white focus:text-white" : "text-[#1A1A1A] focus:text-[#1A1A1A]"}`}
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mb-8">
                    <Label htmlFor="note" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                      Note Aggiuntive
                    </Label>
                    <Textarea
                      id="note"
                      data-testid="form-note"
                      value={formData.note}
                      onChange={(e) => handleChange("note", e.target.value)}
                      placeholder="Numero passeggeri, bagagli, richieste particolari..."
                      rows={4}
                      className={`border rounded-none resize-none ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="form-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      "Invio in corso..."
                    ) : (
                      <>
                        <Send className="w-4 h-4" strokeWidth={1.5} />
                        Richiedi Preventivo Personalizzato
                      </>
                    )}
                  </button>
                </form>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
